import { z } from "zod";
import { IMAGE_MODEL_SHORTLIST_SIZE } from "./image-generation-models";

export const generateImageInChatInputSchema = z.object({
	prompt: z
		.string()
		.min(1)
		.describe(
			"Image generation prompt. May refine the user's ask; do not invent subject matter they did not request.",
		),
	modelIds: z
		.array(z.string().min(1))
		.min(1)
		.max(IMAGE_MODEL_SHORTLIST_SIZE)
		.describe(
			"Gateway modelId strings (1–3). For compare-all-3, pass the same shortlist modelIds just offered.",
		),
});

export type GenerateImageInChatInput = z.infer<
	typeof generateImageInChatInputSchema
>;

export type GenerateImageInChatModelResult =
	| {
			modelId: string;
			status: "ok";
			mediaType: string;
			data: string;
			dataKind: "base64";
			displayName?: string;
	  }
	| {
			modelId: string;
			status: "error";
			errorMessage: string;
			displayName?: string;
	  };

export type GenerateImageInChatOutput = {
	prompt: string;
	results: GenerateImageInChatModelResult[];
};

export type GenerateImageInChatSummary = {
	prompt: string;
	results: Array<{
		modelId: string;
		status: "ok" | "error";
		mediaType?: string;
		errorMessage?: string;
	}>;
};

export function isGenerateImageInChatOutput(
	output: unknown,
): output is GenerateImageInChatOutput {
	if (typeof output !== "object" || output === null) {
		return false;
	}

	const record = output as Record<string, unknown>;
	if (typeof record.prompt !== "string" || !Array.isArray(record.results)) {
		return false;
	}

	return record.results.every((item) => {
		if (typeof item !== "object" || item === null) {
			return false;
		}
		const row = item as Record<string, unknown>;
		if (typeof row.modelId !== "string") {
			return false;
		}
		if (row.status === "ok") {
			return (
				typeof row.mediaType === "string" &&
				typeof row.data === "string" &&
				row.dataKind === "base64"
			);
		}
		if (row.status === "error") {
			return typeof row.errorMessage === "string";
		}
		return false;
	});
}

export function summarizeGenerateImageInChatOutput(
	output: GenerateImageInChatOutput,
): GenerateImageInChatSummary {
	return {
		prompt: output.prompt,
		results: output.results.map((result) => {
			if (result.status === "ok") {
				return {
					modelId: result.modelId,
					status: "ok",
					mediaType: result.mediaType,
				};
			}

			return {
				modelId: result.modelId,
				status: "error",
				errorMessage: result.errorMessage,
			};
		}),
	};
}

/**
 * What the chat model sees after generateImageInChat.
 * Never include base64 — that blows up the next chat/completions step.
 * UI still receives full bytes from tool execute / stream tool-result.
 */
export function generateImageInChatModelOutput(
	output: GenerateImageInChatOutput,
): { type: "json"; value: GenerateImageInChatSummary } {
	return {
		type: "json",
		value: summarizeGenerateImageInChatOutput(output),
	};
}
